// frontend/src/components/SanqiStatusBoard.jsx
import React, { useMemo } from 'react';
import './SanqiStatusBoard.css';

/**
 * SanqiStatusBoard
 * 1:1 还原风格的顶部状态栏（参考截图）。
 * Props:
 * - deviceNo: string | number 设备编号（例如 5855）
 * - username: string 用户名
 * - points: number P 积分
 * - silver: number 银币
 * - score: number 当前分数
 * - difference: number 差值（盈亏等）
 * - gridData: { topLabels:number[]|string[], columns:number, rows:number, filled:number } 可选，渲染中间条形板
 * - clock: string HH:MM:SS 文本钟
 */
export default function SanqiStatusBoard({
  deviceNo,
  username,
  points,
  silver,
  score = 0,
  difference = 0,
  centerItems,
  leftDailyTurns,
  leftDailySeries,
  gridData,
  clock
}) {
  const safePoints = useMemo(() => Number(points || 0).toFixed(2), [points]);
  const safeSilver = useMemo(() => Number(silver || 0).toFixed(0), [silver]);
  const deviceNoText = useMemo(() => `NO.${deviceNo ?? ''}`.toUpperCase(), [deviceNo]);

  const cols = gridData?.columns ?? 12;
  const rows = gridData?.rows ?? 8;
  const filled = gridData?.filled ?? 0; // 简单模式填充高度（每列相同）

  // 直方图模式：根据 rounds 按每100转一格渲染，>1000从底部标红
  const histogramCols = useMemo(() => {
    if (!gridData || gridData.mode !== 'histogram' || !Array.isArray(gridData.rounds)) return null;
    const rounds = [...gridData.rounds];
    // 尝试按时间倒序（若有timestamp）
    rounds.sort((a, b) => {
      const ta = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const tb = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return tb - ta;
    });
    const take = rounds.slice(0, cols);
    return take.map(r => {
      const totalTurns = Number(r.totalTurns ?? r.turnCount ?? 0) || 0;
      const consecutiveWins = Number(r.consecutiveWins ?? r.consecutivePrize ?? 0) || 0;
      const totalGrids = Math.ceil(totalTurns / 100);
      const active = Math.min(rows, Math.max(0, totalGrids));
      const redCount = totalTurns > 1000 ? Math.min(rows, Math.ceil((totalTurns - 1000) / 100)) : 0;
      // 生成自顶向下的行状态：底部开始填充
      const rowStates = Array.from({ length: rows }).map((_, rIdx) => {
        const fromBottomIdx = rows - rIdx; // 1..rows，自底向上
        const isFilled = fromBottomIdx <= active;
        const isRed = fromBottomIdx <= redCount;
        return { filled: isFilled, red: isRed };
      });
      return { totalTurns, consecutiveWins, active, redCount, rowStates };
    });
  }, [gridData, cols, rows]);

  const topLabels = useMemo(() => {
    if (Array.isArray(gridData?.topLabels)) return gridData.topLabels;
    // 默认序列：1 3 4 5 6 0 重复，贴近截图的风格化标签
    const seq = [1, 3, 4, 5, 6, 0];
    return Array.from({ length: cols }, (_, i) => seq[i % seq.length]);
  }, [gridData?.topLabels, cols]);

  return (
    <div className="sanqi-status-board">
      <div className="sanqi-status-grid">
        {/* 左侧表格 */}
        <div className="panel left-panel">
          {Array.isArray(leftDailySeries) && leftDailySeries.length > 0 ? (
            <>
              <div className="left-header left-two-cols">
                <div className="left-cell">今日</div>
                <div className="left-cell">连奖</div>
              </div>
              {leftDailySeries.slice(0, 6).map((d, idx) => (
                <div key={idx} className="left-row left-two-cols">
                  <div className="left-cell left-muted">{Number(d.turns || 0)}</div>
                  <div className="left-cell left-muted">{Number(d.wins || 0)}</div>
                </div>
              ))}
            </>
          ) : (
            <>
              <div className="left-header">
                <div className="left-cell left-strong">B</div>
                <div className="left-cell">今日</div>
                <div className="left-cell">连奖</div>
              </div>
              {Array.from({ length: 6 }).map((_, idx) => (
                <div key={idx} className="left-row">
                  <div className="left-cell left-muted">{idx === 0 ? '' : '0'}</div>
                  <div className="left-cell left-muted">{idx === 0 ? (leftDailyTurns?.today ?? 0) : '0'}</div>
                  <div className="left-cell left-muted">{idx === 0 ? (leftDailyTurns?.winsToday ?? 0) : '0'}</div>
                </div>
              ))}
            </>
          )}
        </div>

        {/* 中间设备号 + 条形网格 + 底部得分区 */}
        <div className="panel center-panel">
          <div className="device-no">{deviceNoText}</div>
          <div className="bar-board">
            {histogramCols ? (
              <div className="bar-top-stats">
                {histogramCols.map((col, i) => (
                  <div key={i} className="turns-number">{col.totalTurns}</div>
                ))}
              </div>
            ) : (
              <div className="bar-top-labels">
                {Array.from({ length: cols }).map((_, i) => (
                  <div key={i} style={{ textAlign: 'center' }}>{topLabels[i] ?? ''}</div>
                ))}
              </div>
            )}
            <div className="bar-grid">
              {histogramCols
                ? histogramCols.map((col, c) => (
                    <div key={c} className="bar-col">
                      {col.rowStates.map((st, r) => (
                        <div key={r} className={`bar-cell${st.filled ? ' filled' : ''}${st.red ? ' red' : ''}`} />
                      ))}
                    </div>
                  ))
                : Array.from({ length: cols }).map((_, c) => (
                    <div key={c} className="bar-col">
                      {Array.from({ length: rows }).map((_, r) => (
                        <div key={r} className={`bar-cell ${r < Math.min(rows, filled) ? 'filled' : ''}`} />
                      ))}
                    </div>
                  ))}
            </div>
            {histogramCols ? (
              <div className="bar-bottom-stats">
                {histogramCols.map((col, i) => (
                  <div key={i} className="wins-number">{col.consecutiveWins}</div>
                ))}
              </div>
            ) : (
              <>
                <div className="bar-bottom-labels">
                  {Array.from({ length: cols }).map((_, i) => (
                    <div key={i} style={{ textAlign: 'center' }}>{topLabels[i] ?? ''}</div>
                  ))}
                </div>
                <div className="bar-baseline" />
              </>
            )}
          </div>
          {(() => {
            const items = Array.isArray(centerItems) && centerItems.length > 0
              ? centerItems
              : [
                  { label: '差枚数', value: Number(score || 0) },
                  { label: '剩余转数', value: Number(difference || 0) }
                ];
            const cols = Math.max(2, items.length);
            return (
              <div className="score-area" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
                {items.map((it, idx) => (
                  <div key={idx} className="kv"><span className="label">{it.label}</span><span className="value">{it.value}</span></div>
                ))}
              </div>
            );
          })()}
        </div>

        {/* 右侧用户名 + P + 银币 + 时钟 */}
        <div className="panel right-panel">
          <div className="user-name">{username || '-'}</div>
          <div className="points-row">
            <div className="p-badge">P</div>
            <div className="p-amount">{safePoints}</div>
          </div>
          <div className="coins-row">
            <div className="coin-dot" />
            <div className="coin-amount">{safeSilver}</div>
          </div>
          <div className="clock">{clock || '--:--:--'}</div>
        </div>
      </div>
    </div>
  );
}
